
import * as CryptoJS from 'crypto-js';
import { publish } from 'js/accEventBus';
import { get, isNil, isEmpty, set } from 'lodash';
import AwHttpService from 'js/awHttpService';
import AccHttpService from 'js/accHttpService';
const FILE_CHOSEN = 'data.fileChosen';

/**
 * Import data upload service.
 */
class DataImportService {

    //#region class variables sections
    /**
     * Holds aw files
     */
    awFiles = null;

    /**
     * Holds aw view model instance
     */
    awViewModel = null;

    /**
     * Holds acc properties
     */
    accProps = null;
    /**
     * Holds staged file objects
     */
    stagedObjects = null;

    /**
     * Holds lcs collabspace id
     */
    collaborationspaceId = null;

    accSession = {
        accsessionid: null,
        accendpoint: null
    };

    processingFileIndex = 0;

    /**
     * Flag to indicates resource is busy or not.
     */
    resourceBusy = false;

    //#endregion class variables sections
    /**
     * Constructor fn
     * @returns {this} class instance
     */
    constructor() {
        if (DataImportService.instance) {
            DataImportService.instance = this;
        }
        return DataImportService.instance;
    }


    /**
     * Init imports.
     *
     * @param {Array} awFiles aw files objects
     * @param {Object} accProps acc props
     * @param {Object} stagedObjects stagedObjects
     * @param {*} viewModel aw view model object
     */
    async initImport(awFiles, accProps, stagedObjects, viewModel) {
        try {
            // Allowcate resource
            this.resourceBusy = true;
            this.awFiles = awFiles;
            this.accProps = accProps;
            this.stagedObjects = stagedObjects;
            this.awViewModel = viewModel;
            // Add unload event
            // Commented: this.toggleUnloadEvent('add');
            // set csId
            this.setCollabspaceId();
            // decode acc-session-id
            this.decodeAccsessionId();
            // Begin file upload
            await this.startImport();
            // Perform imports
            const taskId = await this.performImport();
            // Poll task status
            // Commented: await this.pollTaskStatus(taskId);
            await this.importCompleted(taskId);
            // Relax the resource.
            this.resourceBusy = false;
        } catch (initError) {
            this.resourceBusy = false;
            // Remove unload event
            // Commented: this.toggleUnloadEvent('remove');
            await this.toggleStatus(get(this.awViewModel.i18n, 'uploadError'));
            throw initError;
        }
    }

    /**
     * Start import process.
     */
    async startImport() {
        try {
            //
            const results = await this.executeRequestsInBatches();
            // Process results
            results.forEach((result, index) => {
                console.log(`Request no. ${index + 1}, success: ${result.success}, data: ${JSON.stringify(result.data, null, 2)}`);
            });
            console.log('All requests completed.');
        } catch (err) {
            console.log('Error occured in data import operation', err);
            throw err;
        }
    }

    async executeRequestsInBatches() {
        try {
            // Batch size 50
            const batchSize = 50;
            // Adjust concurrency limit based on system/network constraints
            const concurrencyLimit = 20;
            const totalRequests = this.awFiles.length;
            let results = [];
            let currentBatch = 0;
            let errorMsg = null;
            while ( currentBatch < totalRequests ) {
                try {
                    const batchFiles = this.awFiles.slice(currentBatch, currentBatch + batchSize);
                    const batchPromises = batchFiles.map(awFile => {
                        const counter = this.processingFileIndex;
                        this.processingFileIndex++;
                        return this.makeHttpRequest(awFile, counter)
                    });
                    // Execute batch requests with limited concurrency
                    const batchResults = await this.executeWithConcurrency(batchPromises, concurrencyLimit);
                    results = results.concat(batchResults);
                    currentBatch += batchSize;
                } catch(error) {
                    console.log('Error inside in while catch block', error);
                    errorMsg = error;
                    break;
                }
            }
            if (!isNil(errorMsg)) {
                throw errorMsg;
            }
            return results;
        } catch (err) {
            console.log('Error occured while executing requests in bacthes', err);
            throw err;
        }
    }

    /**
     * Make http request
     * @param {Object} awFile aw file object
     * @param {Number} indexNo object index number
     * @returns
     */
    async makeHttpRequest(awFile, indexNo) {
        return new Promise(async (resolve, reject) => {
            try {
                let httpResponse = {};
                const uploadTicket = this.getUploadTicket(awFile, indexNo);
                if (this.isLargeFile(awFile.file.size)) {
                    // process large dataset
                    await this.processLargeDataset(awFile, uploadTicket);
                    httpResponse = { id: indexNo, status: 'done' };
                } else {
                    // Change file status from Pending to Uploading
                    set(awFile, 'file.status', get(this.awViewModel.i18n, 'uploading'));
                    this.updateObject(awFile.file);
                    // Form data
                    var formData = new FormData();
                    formData.append('dssFile', awFile.file);
                    await AwHttpService.instance.post(`${this.accSession.accendpoint}/acc/importer`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'x-siemens-accsessionid': this.accSession.accsessionid,
                            'x-siemens-file-length': awFile.file.size,
                            'x-siemens-file-ticket': uploadTicket
                        }
                    });
                    // Update file uploaded status
                    awFile.file.status = get(this.awViewModel.i18n, 'uploaded');
                    this.updateObject(awFile.file);
                    httpResponse = { id: indexNo, status: 'done' };
                }
                resolve(httpResponse);
            } catch (httpError) {
                reject(httpError);
                throw httpError;
            }
        })
    }

    async executeWithConcurrency(promises, concurrencyLimit) {
        const results = [];
        // Track ongoing promises
        const executing = [];
        for (const promise of promises) {
            // Start promise execution
            const promiseExecution = promise.then(result => {
                results.push({ success: true, data: result });
            }).catch(error => {
                const errObj = {
                    data: error,
                    success: false
                };
                results.push(errObj);
                throw errObj;
            });
            // Add to executing array
            executing.push(promiseExecution);
            // Wait until we have less than concurrencyLimit promises executing
            if (executing.length >= concurrencyLimit) {
                // Wait for any of the executing promises to resolve
                await Promise.race(executing);
            }
        }
        // Wait for all remaining promises to finish
        await Promise.all(executing);
        console.log('processed file results:', results);
        return results;
    }

    async performImport() {
        const output = await AccHttpService.createHttpRequest(
            {
                method: 'POST',
                url: '/acc/orchestrate',
                withCredentials: true,
                data: {
                    additionalProperties: {},
                    query: 'mutation performImport($performImportInput: PerformImportInput) { performImport(performImportInput: $performImportInput) }',
                    variables: {
                        performImportInput: {
                            collaborationspaceId: this.collaborationspaceId,
                            containerType: get(this.accProps, 'containertype'),
                            containerId: get(this.accProps, 'containerid'),
                            stagingAreaId: get(this.stagedObjects, 'stagingAreaId')
                        }
                    }
                }
            },
            'collab',
            get(this.accProps, 'accsessionid')
        );
        const taskId = get(output, 'data.data.performImport');
        if (isNil(taskId) || isEmpty(taskId)) {
            throw new Error('Failed to execute perform import task.');
        }
        return taskId;
    }

    /**
     * Poll perform import task status.
     * 
     * @param {String} taskId 
     */
    async pollTaskStatus(taskId) {
        // Poll task status
        let status = 'Accepted';
        while (status === 'Executing' || status === 'Accepted' || status === 'NotStarted') {
            const statusRes = await AccHttpService.createHttpRequest(
                {
                    method: 'POST',
                    url: '/acc/orchestrate',
                    withCredentials: true,
                    data: {
                        query: 'query CheckStatus($taskId: String) { checkStatus(taskId: $taskId) }',
                        variables: {
                            taskId: taskId
                        }
                    }
                },
                'collab',
                get(this.accProps, 'accsessionid')
            );
            status = get(statusRes, 'data.data.checkStatus.status');
            await this.toggleStatus(`${get(this.awViewModel.i18n, 'import')}: ${status}`);
            this.dispatch();
            if (status === 'Executing' || status === 'Accepted' || status === 'NotStarted') {
                await new Promise(r => setTimeout(r, this.getDelayTime()));
            }
        }
        if ( status === 'Failed' ) {
            throw new Error(`Import failed`);
        }
    }

    async processLargeDataset(awFile, uploadTicket) {
        // Default chunk size: 20MB
        const chunkSize = 20 * 1024 * 1024;
        let offset = 0;
        let chunkIndex = 0;

        // Total number of chunks. Required for DSS multipart upload
        let totalChunks = parseInt('' + (awFile.file.size / chunkSize), 10);
        if (awFile.file.size % chunkSize > 0) {
            totalChunks++;
        }
        while (offset < awFile.file.size) {
            // Define variables
            const chunk = awFile.file.slice(offset, offset + chunkSize);
            const formData = new FormData();
            // Populate input
            formData.append('dssChunk', chunk, awFile.file.name);
            const res = await AwHttpService.instance.post(`${this.accSession.accendpoint}/acc/partupload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'x-siemens-file-length': awFile.file.size,
                    'x-siemens-accsessionid': this.accSession.accsessionid,
                    'x-siemens-file-ticket': uploadTicket,
                    // Chunks
                    'x-siemens-total-chunks': totalChunks, // Toatal number of chunks
                    'x-siemens-chunk-limit': chunkSize, // Chunk limit
                    'x-siemens-chunk-index': chunkIndex + 1, // Chunk index number
                    'x-siemens-chunk-size': chunk.size, // Chunk size
                    'x-siemens-last-chunk': (offset + chunkSize >= awFile.file.size), // Last chunk
                    // This is required for large file chunk uploads
                    'x-siemens-chunk-content-type': awFile.file.type
                }
            });
            console.log('Chunkupload response:', res);
            offset += chunkSize;
            chunkIndex++;
        }
        return true;
    }

    /**
     * Set collaboration space ID
     */
    setCollabspaceId() {
        let csId = get(this.accProps, 'collaborationspaceid')
        if (isNil(csId) || isEmpty(csId)) {
            csId = get(this.stagedObjects, 'collaborationspaceId');
        }
        this.collaborationspaceId = csId;
    }

    /**
     * Decode acc session id
     */
    decodeAccsessionId() {
        const encodedWord = CryptoJS.enc.Base64.parse(get(this.accProps, 'accsessionid'));
        const decoded = CryptoJS.enc.Utf8.stringify(encodedWord);
        const session = JSON.parse(decoded);
        this.accSession.accsessionid = session.accsessionid;
        this.accSession.accendpoint = session.accendpoint;
    }

    /**
     * Get upload ticket.
     *
     * @param {Object} awFile file object
     * @param {*} indexNo file index
     * @returns {string} upload ticket
     */
    getUploadTicket(awFile, indexNo) {
        // First get upload ticket from stagedObjects
        let uploadTicket = null;
        let stagedFile = get(this.stagedObjects, `results.${indexNo}`);
        console.log(`Staged files are: ${indexNo}`, stagedFile);
        const awFileName = get(awFile, 'file.name');
        // Cross check file name
        if (get(stagedFile, 'data.fileName') === awFileName) {
            uploadTicket = get(stagedFile, 'data.uploadTicket');
        } else {
            console.log('File name does not match in IF condition');
            stagedFile = this.stagedObjects.results.find(r => get(r, 'data.fileName') === awFileName);
            uploadTicket = get(stagedFile, 'data.uploadTicket');
        }
        if (isNil(uploadTicket) || isEmpty(uploadTicket)) {
            throw new Error(JSON.stringify({
                message: `Something went wrong. upload ticket not found against filename: ${awFileName}`
            }));
        }
        return uploadTicket;
    }

    /**
     * 
     * @param {Object} awFile file object
     */
    updateObject(awFile) {
        publish('des-import-data.uploadStatus', { fileName: awFile['name'], status: awFile['status'] });
    }

    dispatch() {
        this.awViewModel.dispatch({ path: FILE_CHOSEN, value: { now: false } });
    }

    /**
     * Toggle file status.
     *
     * @param {string} status
     * @returns
     */
    toggleStatus(status) {
        return new Promise((resolve, reject) => {
            try {
                for (const fileEle of this.awFiles) {
                    fileEle.file.status = status;
                    this.updateObject(fileEle.file);
                }
                this.dispatch();
                resolve(true)
            } catch (err) {
                reject(err);
            }
        });
    }

    /**
     * Resource is busy or not.
     *
     * @returns Boolean.
     */
    isResourceBusy() {
        return this.resourceBusy;
    }

    /**
     * Toggle unload event.
     *
     * @param {string} type
     */
    toggleUnloadEvent(type) {
        if (type === 'add') {
            window.addEventListener('beforeunload', this.unloadEventHandler);
        } else {
            window.removeEventListener('beforeunload', this.unloadEventHandler);
        }
    }

    /**
     * Browser beforeunload event handler.
     *
     * @param {Event} event
     * @returns confirmation.
     */
    unloadEventHandler(event) {
        // Alert user if resource is busy
        if (this.isResourceBusy()) {
            // Cancel the event
            event.preventDefault();
            // Chrome requires returnValue to be set
            event.returnValue = '';
            // Comment: use i18n later
            return 'Are you sure you want to leave this page? Your changes may not be saved.'
        } else {
            return false;
        }
    }

    getDelayTime() {
        let defaultDelay = 5000;

        // Define a mapping for fileLength ranges to defaultDelay values
        const delayMap = [
            { max: 50, delay: 10000 },
            { max: 100, delay: 15000 },
            { max: 1000, delay: 20000 },
            { max: 5000, delay: 25000 },
            { max: 10000, delay: 30000 }
        ];

        const fileLength = this.awFiles.length;
        // Find the appropriate delay based on fileLength
        for (let i = 0; i < delayMap.length; i++) {
            if (fileLength <= delayMap[i].max) {
                defaultDelay = delayMap[i].delay;
                break; // Exit loop once the correct delay is found
            }
        }

        // Default case: if fileLength exceeds all defined ranges
        if (fileLength > delayMap[delayMap.length - 1].max) {
            defaultDelay = 35000; // or any other default delay you want
        }
        return defaultDelay;
    }

    isLargeFile(fileSize) {
        const maxChunkSize = 20 * 1024 * 1024; // 10 MB in bytes
        return fileSize > maxChunkSize;
    }

    importCompleted(id) {
        return new Promise((resolve) => {
            // Notify user
            for (const fileEle of this.awFiles) {
                fileEle.file.status = get(this.awViewModel.i18n, 'uploadCompleted');
                this.updateObject(fileEle.file);
            }
            publish('des-import-data.importCompleted', { taskId: id });
            resolve(true);
        });
    }
}

const dataImportService = new DataImportService();

export default dataImportService;
